import { useQuery, useMutation, useQueryClient, UseQueryResult, UseMutationResult } from '@tanstack/react-query';
import { App } from 'antd';
import materialReturnService, {
  MaterialReturn,
  CreateMaterialReturnDTO,
} from '../services/materialReturn.service';

export const useMaterialReturns = (filters?: {
  status?: string;
  startDate?: string;
  endDate?: string;
}): UseQueryResult<MaterialReturn[], Error> => {
  return useQuery({
    queryKey: ['material-returns', filters],
    queryFn: () => materialReturnService.getAll(filters),
  });
};

export const useMaterialReturn = (
  id: string,
  options?: { enabled?: boolean }
): UseQueryResult<MaterialReturn, Error> => {
  return useQuery({
    queryKey: ['material-returns', id],
    queryFn: () => materialReturnService.getById(id),
    enabled: options?.enabled !== false && !!id,
  });
};

export const useAcceptMaterialReturn = (): UseMutationResult<MaterialReturn, Error, string> => {
  const queryClient = useQueryClient();
  const { message } = App.useApp();
  return useMutation({
    mutationFn: (id) => materialReturnService.accept(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['material-returns'] });
      message.success('Return accepted');
    },
    onError: (error: any) => {
      message.error(error.response?.data?.message || 'Failed to accept return');
    },
  });
};

export const useRejectMaterialReturn = (): UseMutationResult<
  MaterialReturn,
  Error,
  { id: string; reason?: string }
> => {
  const queryClient = useQueryClient();
  const { message } = App.useApp();
  return useMutation({
    mutationFn: ({ id, reason }) => materialReturnService.reject(id, reason),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['material-returns'] });
      message.success('Return rejected');
    },
    onError: (error: any) => {
      message.error(error.response?.data?.message || 'Failed to reject return');
    },
  });
};

export const useCreateMaterialReturn = (): UseMutationResult<
  MaterialReturn,
  Error,
  CreateMaterialReturnDTO
> => {
  const queryClient = useQueryClient();
  const { message } = App.useApp();
  return useMutation({
    mutationFn: (data) => materialReturnService.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['material-returns'] });
      message.success('Return recorded');
    },
    onError: (error: any) => {
      message.error(error.response?.data?.message || 'Failed to create return');
    },
  });
};
